/**
 * WeeklyReviewPage — J5 B2
 *
 * The weekly operating review execution surface.
 * Route: /roros/cadence/weekly-review
 *
 * Sections:
 *  1. FTT trend (current week vs trailing 4) — Recharts line chart
 *  2. Open work items by status — status buckets with counts
 *  3. Decision latency — averageDays / medianDays / p90Days
 *  4. 3 Mu's panel — Muda / Muri / Mura
 *  5. "Complete review" button → dialog → submits to completeReview
 *
 * PRD §12.3.8 task 2.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, BarChart2, GitBranch, CheckSquare, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { t, onDictionaryChange } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { CompleteWeeklyReviewSheet } from './CompleteWeeklyReviewSheet';
import { ThreeMusPanel } from '@/components/roros/cadence/ThreeMusPanel';
import { useRorosCadenceReviews, useRorosCadenceReviewComplete, useRorosCadenceReviewCreate } from '@/hooks/roros/cadence';
import { useRorosFttSummary } from '@/hooks/roros/quality';
import { useRorosDecisionLatency } from '@/hooks/roros/decisions';
import { useRorosWorkItems } from '@/hooks/roros/control-tower';
import { useRorosThreeMus } from '@/hooks/roros/three-mus';
import { formatDate, formatNumber } from '@/lib/localeFormatters';
import type { RorosCadenceReviewDTO } from '@/services/RorosService';

// ─── Section card wrapper ───────────────────────────────────────────────────

function SectionCard({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4 sm:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        {icon && <div className="flex-shrink-0 text-muted-foreground">{icon}</div>}
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ─── FTT chart ──────────────────────────────────────────────────────────────

function FttTrendChart({ data: fttData }: { data: any }) {
  const weeks: any[] =
    fttData?.weeks ?? fttData?.trend ?? fttData?.periods ?? fttData?.dataPoints ?? [];

  const chartData = useMemo(() =>
    weeks.map((w: any, i: number) => ({
      label: w.weekLabel ?? w.label ?? w.period ?? `W${i + 1}`,
      ftt: typeof w.fttRate === 'number'
        ? Math.round(w.fttRate * 1000) / 10
        : typeof w.rate === 'number'
          ? Math.round(w.rate * 1000) / 10
          : null,
    })),
    [weeks]
  );

  if (chartData.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('No FTT data available yet.')}</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11 }}
          className="text-muted-foreground"
        />
        <YAxis
          tickFormatter={(v) => `${v}%`}
          domain={[0, 100]}
          tick={{ fontSize: 11 }}
          className="text-muted-foreground"
        />
        <RechartsTooltip
          formatter={(v) => {
            const pct = typeof v === 'number' ? `${v}%` : String(v ?? '');
            return [pct, t('FTT')];
          }}
        />
        <Line
          type="monotone"
          dataKey="ftt"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Work item status buckets ────────────────────────────────────────────────
// STATUS_LABELS moved inside component body (useMemo) — CRIT-C2 fix.
// Module-level t() calls freeze to English and never re-resolve on locale change.

function WorkItemStatusBuckets({ items }: { items: any[] }) {
  // Re-resolve labels when dictionary changes (locale switch)
  const [dictVersion, setDictVersion] = useState(0);
  useEffect(() => onDictionaryChange(() => setDictVersion((v) => v + 1)), []);

  const STATUS_LABELS = useMemo<Record<string, string>>(
    () => ({
      backlog: t('Backlog'),
      ready: t('Ready'),
      in_progress: t('In Progress'),
      quality_confirmation: t('QC'),
      released: t('Released'),
      escalated: t('Escalated'),
      cancelled: t('Cancelled'),
      blocked: t('Blocked'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dictVersion],
  );

  const counts: Record<string, number> = {};
  for (const item of items) {
    const s = item.status ?? 'unknown';
    counts[s] = (counts[s] ?? 0) + 1;
  }
  const entries = Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('No open work items.')}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([status, count]) => (
        <div
          key={status}
          className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-1.5"
        >
          <span className="text-xs text-muted-foreground capitalize">
            {STATUS_LABELS[status] ?? status.replace(/_/g, ' ')}
          </span>
          <span className="text-sm font-semibold tabular-nums text-foreground">{count}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Decision latency stats ──────────────────────────────────────────────────

function DecisionLatencyStats({ data }: { data: any }) {
  if (!data) return <p className="text-sm text-muted-foreground">{t('No latency data available.')}</p>;
  const avg = data.averageDays ?? data.average_days ?? data.avg ?? null;
  const median = data.medianDays ?? data.median_days ?? data.median ?? null;
  const p90 = data.p90Days ?? data.p90_days ?? data.p90 ?? null;
  return (
    <div className="flex flex-wrap gap-6">
      {avg !== null && (
        <div>
          <p className="text-2xl font-bold tabular-nums text-foreground">{formatNumber(avg)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('Avg days')}</p>
        </div>
      )}
      {median !== null && (
        <div>
          <p className="text-2xl font-bold tabular-nums text-foreground">{formatNumber(median)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('Median days')}</p>
        </div>
      )}
      {p90 !== null && (
        <div>
          <p className="text-2xl font-bold tabular-nums text-foreground">{formatNumber(p90)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('P90 days')}</p>
        </div>
      )}
      {avg === null && median === null && p90 === null && (
        <p className="text-sm text-muted-foreground">{t('Insufficient data for latency calculation.')}</p>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WeeklyReviewPage() {
  const navigate = useNavigate();
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const reviewsQuery = useRorosCadenceReviews({ reviewType: 'weekly', status: 'open' } as Record<string, string>);
  const createReviewMutation = useRorosCadenceReviewCreate();
  const completeReviewMutation = useRorosCadenceReviewComplete();

  const fttQuery = useRorosFttSummary();
  const latencyQuery = useRorosDecisionLatency();
  const workItemsQuery = useRorosWorkItems();
  const threeMus = useRorosThreeMus(7);

  // Pick the most recent open weekly review (or the first one returned)
  const reviews: RorosCadenceReviewDTO[] = Array.isArray(reviewsQuery.data)
    ? reviewsQuery.data
    : (reviewsQuery.data as any)?.items ?? [];
  const activeReview = reviews[0] ?? null;

  // Determine work items array
  const allWorkItems: any[] = Array.isArray(workItemsQuery.data)
    ? workItemsQuery.data
    : (workItemsQuery.data as any)?.items ?? [];

  const handleCompleteSubmit = (payload: {
    summary: string;
    action_items: string;
    three_mus_notes: string;
  }) => {
    if (!activeReview) {
      // If no review exists, create one first then complete it
      createReviewMutation.mutate(
        { review_type: 'weekly', cadence_type: 'weekly' },
        {
          onSuccess: (newReview: any) => {
            completeReviewMutation.mutate(
              {
                id: newReview.id,
                payload: {
                  summary: payload.summary,
                  action_items: payload.action_items
                    ? payload.action_items.split('\n').filter(Boolean).map((a) => ({ text: a }))
                    : [],
                  three_mus_notes: payload.three_mus_notes,
                },
              },
              {
                onSuccess: () => {
                  setIsCompleteDialogOpen(false);
                  setCompleteError(null);
                  navigate(0); // refresh
                },
                onError: (err: any) => {
                  setCompleteError(err?.message ?? t('Failed to complete review'));
                },
              },
            );
          },
          onError: (err: any) => {
            setCompleteError(err?.message ?? t('Failed to create review'));
          },
        },
      );
      return;
    }

    completeReviewMutation.mutate(
      {
        id: activeReview.id,
        payload: {
          summary: payload.summary,
          action_items: payload.action_items
            ? payload.action_items.split('\n').filter(Boolean).map((a) => ({ text: a }))
            : [],
          three_mus_notes: payload.three_mus_notes,
        },
      },
      {
        onSuccess: () => {
          setIsCompleteDialogOpen(false);
          setCompleteError(null);
          reviewsQuery.refetch();
        },
        onError: (err: any) => {
          setCompleteError(err?.message ?? t('Failed to complete review'));
        },
      },
    );
  };

  const isPending = completeReviewMutation.isPending || createReviewMutation.isPending;

  return (
    <RorosPage
      title={t('Weekly Operating Review')}
      icon={<CalendarClock className="h-5 w-5" aria-hidden />}
      breadcrumbs={[{ label: t('Cadence'), path: '/roros/cadence' }]}
      actions={
        <Button
          size="sm"
          onClick={() => setIsCompleteDialogOpen(true)}
          disabled={isPending}
          data-testid="complete-review-btn"
        >
          <CheckSquare className="h-4 w-4 mr-1.5" aria-hidden />
          {t('Complete Review')}
        </Button>
      }
    >
      {completeError && (
        <Alert variant="error" title={t('Error')} className="mb-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{completeError}</span>
          </div>
        </Alert>
      )}

      <div className="mt-4 space-y-4">
        {/* Active review banner */}
        {activeReview && (
          <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50/40 px-4 py-2.5">
            <div className="text-sm text-primary-700">
              {t('Active review started') + ' ' + formatDate(new Date(activeReview.createdAt))}
            </div>
            <span className="text-xs font-medium text-primary-600 bg-primary-100 rounded-full px-2 py-0.5">
              {t('Open')}
            </span>
          </div>
        )}

        {/* Section 1: FTT trend */}
        <SectionCard title={t('FTT Trend (last 5 weeks)')} icon={<BarChart2 className="h-4 w-4" aria-hidden />}>
          {fttQuery.isLoading ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">
              {t('Loading FTT data…')}
            </div>
          ) : fttQuery.isError ? (
            <p className="text-sm text-error-600">{t('Failed to load FTT data.')}</p>
          ) : (
            <FttTrendChart data={fttQuery.data} />
          )}
        </SectionCard>

        {/* Section 2: Open work items by status */}
        <SectionCard title={t('Open Work Items by Status')} icon={<BarChart2 className="h-4 w-4" aria-hidden />}>
          {workItemsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('Loading work items…')}</p>
          ) : workItemsQuery.isError ? (
            <p className="text-sm text-error-600">{t('Failed to load work items.')}</p>
          ) : (
            <WorkItemStatusBuckets items={allWorkItems} />
          )}
        </SectionCard>

        {/* Section 3: Decision latency */}
        <SectionCard title={t('Decision Latency')} icon={<GitBranch className="h-4 w-4" aria-hidden />}>
          {latencyQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('Loading latency data…')}</p>
          ) : latencyQuery.isError ? (
            <p className="text-sm text-error-600">{t('Failed to load latency data.')}</p>
          ) : (
            <DecisionLatencyStats data={latencyQuery.data} />
          )}
        </SectionCard>

        {/* Section 4: 3 Mu's */}
        <SectionCard title={t("3 Mu's Diagnostics")} icon={<BarChart2 className="h-4 w-4" aria-hidden />}>
          <ThreeMusPanel data={threeMus} periodDays={7} />
        </SectionCard>
      </div>

      {/* Complete review sheet — Wave 6 redesign (R1.b: textarea → Sheet) */}
      <CompleteWeeklyReviewSheet
        isOpen={isCompleteDialogOpen}
        onClose={() => {
          setIsCompleteDialogOpen(false);
          setCompleteError(null);
        }}
        onSubmit={handleCompleteSubmit}
        isPending={isPending}
      />
    </RorosPage>
  );
}
